import { catalogNameToFhir, DIAGNOSTIC_DEFINITION_KIND, getTRPCClient, syncKey } from "@repo/api";
import {
  type ChangeDiagnosticDefinitionAvailabilityInput,
  type CorrectDiagnosticDefinitionInput,
  changeDiagnosticDefinitionAvailabilityInputSchema,
  correctDiagnosticDefinitionInputSchema,
  type DefineDiagnosticDefinitionInput,
  defineDiagnosticDefinitionInputSchema,
  type ReviseDiagnosticDefinitionInput,
  reviseDiagnosticDefinitionInputSchema,
} from "@repo/api/schemas";
import type { StandardObservationKey, StandardParameterReference } from "@repo/domain";
import { mapAuthored, mintEntityId } from "@repo/domain";
import { canonicalBase, canonicalUrl } from "@repo/fhir-systems";
import { and, eq, ilike, inArray, max, or, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import type { CatalogValueDto } from "../fhir/catalog-value";
import {
  type DiagnosticDefinitionRowContent,
  type DiagnosticDefinitionRowSpine,
  writeDiagnosticDefinitionRow,
} from "../fhir/diagnostic-definition-storage";
import type { ActivityDefinitionRow } from "../fhir/types";
import { projectDiagnosticDefinitionRow } from "./diagnostic-definition-projection";

const ACTIVITY_DEFINITION_COLLECTION = "activityDefinition";
/** Every diagnostic canonical this app mints sits directly under this base. */
export const DIAGNOSTIC_DEFINITION_CANONICAL_BASE = canonicalBase("ActivityDefinition");

export type DiagnosticDefinitionAvailability = "Active" | "Inactive";

export type DiagnosticValueDefinitionDto = CatalogValueDto;

export interface DiagnosticComponentDto {
  id: string;
  label: string;
  value: DiagnosticValueDefinitionDto;
}

export interface CatalogDiagnosticObservationDto {
  id: string;
  label: string;
  repeatable: boolean;
  noteLabel?: string;
  components: readonly DiagnosticComponentDto[];
}

export interface DiagnosticParameterDto {
  id: string;
  label: string;
  value: DiagnosticValueDefinitionDto;
}

export interface DiagnosticDeviceRequirementDto {
  id: string;
  definitionUrl: string;
}

/** UI read model for diagnostic catalog authoring and detail workflows. */
export interface DiagnosticDefinitionDto {
  definitionUrl: string;
  /** Backing ActivityDefinition identity required by legacy Protocol writes. */
  resourceId?: string;
  version: number;
  mintedAt: string;
  name: string;
  description?: string;
  availability: DiagnosticDefinitionAvailability;
  procedureNoteLabel?: string;
  effectiveParameters: readonly Exclude<DiagnosticDefinitionDto["parameters"][number], StandardParameterReference>[];
  standardObservations: readonly StandardObservationKey[];
  /** Authored only. What the catalog editor edits. */
  observations: readonly CatalogDiagnosticObservationDto[];
  /**
   * What a clinician actually charts: the enabled standards followed by the
   * authored ones, mirroring the domain's `chartedObservations`. Charting
   * surfaces must read this — reading `observations` silently drops Tolerance.
   */
  chartedObservations: readonly CatalogDiagnosticObservationDto[];
  parameters: readonly (DiagnosticParameterDto | StandardParameterReference)[];
  requirements: {
    devices: readonly DiagnosticDeviceRequirementDto[];
    locationType?: string;
  };
}

export interface DiagnosticDefinitionFilters {
  availability?: DiagnosticDefinitionAvailability;
  search?: string;
  /** Exact immutable revisions required by an aggregate read model. */
  canonicals?: readonly string[];
}

const activityDefinitions = () =>
  getDataStoreClient().getCollection<ActivityDefinitionRow, string>(ACTIVITY_DEFINITION_COLLECTION);

/** Mint once before dispatch so optimistic and authoritative writes share lineage identity. */
export function mintDiagnosticDefinitionUrl(): string {
  return canonicalUrl("ActivityDefinition", mintEntityId());
}

/** Current revision of one diagnostic lineage. */
export function useDiagnosticDefinition(definitionUrl: string | undefined): DiagnosticDefinitionDto | undefined {
  const collection = useCollection(ACTIVITY_DEFINITION_COLLECTION);
  const result = useLiveQuery({
    queryKey: [collection.id, "diagnostic-definition", definitionUrl ?? null],
    query: (query) => {
      if (!definitionUrl) return undefined;
      return (
        query
          .from({ definition: collection })
          .where(({ definition }) =>
            and(eq(definition.catalogModelKind, DIAGNOSTIC_DEFINITION_KIND), eq(definition.url, definitionUrl)),
          )
          .orderBy(({ definition }) => definition.catalogVersion, "desc")
          // FHIR contained resources and nested extensions have no declarative query expression.
          .fn.select(({ definition }) => projectDiagnosticDefinitionRow(definition))
          .findOne()
      );
    },
  });
  return result.data;
}

/** Current revision of every diagnostic lineage matching the filters. */
export interface DiagnosticDefinitionsResult {
  definitions: DiagnosticDefinitionDto[];
  isLoading: boolean;
  isReady: boolean;
}

export function useDiagnosticDefinitionsResult(filters: DiagnosticDefinitionFilters = {}): DiagnosticDefinitionsResult {
  const collection = useCollection(ACTIVITY_DEFINITION_COLLECTION);
  const status =
    filters.availability === undefined ? undefined : filters.availability === "Active" ? "active" : "retired";
  const search = filters.search?.trim();
  const canonicals = filters.canonicals;
  const result = useLiveQuery({
    queryKey: [
      collection.id,
      "diagnostic-definitions",
      status ?? null,
      search ?? null,
      canonicals?.length ?? -1,
      ...(canonicals ?? []),
    ],
    query: (query) => {
      if (canonicals) {
        if (canonicals.length === 0) return undefined;
        let exact = query
          .from({ definition: collection })
          .where(({ definition }) => eq(definition.catalogModelKind, DIAGNOSTIC_DEFINITION_KIND))
          .where(({ definition }) =>
            canonicals.length === 1
              ? eq(definition.definitionCanonical, canonicals[0] as string)
              : inArray(definition.definitionCanonical, [...canonicals]),
          );
        if (status) exact = exact.where(({ definition }) => eq(definition.status, status));
        if (search) {
          exact = exact.where(({ definition }) =>
            or(ilike(definition.title, `%${search}%`), ilike(definition.description, `%${search}%`)),
          );
        }
        return exact
          .orderBy(({ definition }) => definition.title, "asc")
          .select(({ definition }) => ({ ...definition }));
      }
      const latestVersions = query
        .from({ candidate: collection })
        .where(({ candidate }) => eq(candidate.catalogModelKind, DIAGNOSTIC_DEFINITION_KIND))
        .groupBy(({ candidate }) => candidate.url)
        .select(({ candidate }) => ({ url: candidate.url, version: max(candidate.catalogVersion) }));
      const current = query
        .from({ definition: collection })
        .join({ latest: latestVersions }, ({ definition, latest }) => eq(definition.url, latest.url))
        .where(({ definition, latest }) =>
          and(
            eq(definition.catalogModelKind, DIAGNOSTIC_DEFINITION_KIND),
            eq(definition.catalogVersion, latest.version),
          ),
        );
      const available = status ? current.where(({ definition }) => eq(definition.status, status)) : current;
      const matching = search
        ? available.where(({ definition }) =>
            or(ilike(definition.title, `%${search}%`), ilike(definition.description, `%${search}%`)),
          )
        : available;
      return matching
        .orderBy(({ definition }) => definition.title, "asc")
        .select(({ definition }) => ({ ...definition }));
    },
  });
  const definitions = ((result.data as ActivityDefinitionRow[] | undefined) ?? []).map(projectDiagnosticDefinitionRow);
  return { definitions, isLoading: result.isLoading, isReady: result.isReady };
}

export function useDiagnosticDefinitions(filters: DiagnosticDefinitionFilters = {}): DiagnosticDefinitionDto[] {
  return useDiagnosticDefinitionsResult(filters).definitions;
}

function optimisticId(definitionUrl: string, version: number): string {
  let hash = 2166136261;
  for (const character of definitionUrl) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return `optimistic-diagnostic-${(hash >>> 0).toString(36)}-${version}`;
}

function memberId(id: string | undefined, prefix: string, index: number): string {
  return id ?? `${prefix}-${index + 1}`;
}

type DiagnosticContent = Pick<
  DefineDiagnosticDefinitionInput,
  "procedureNoteLabel" | "standardObservations" | "observations" | "parameters" | "requirements"
>;

/**
 * The authored content with every member id settled, which is the whole of
 * what the tRPC input leaves open: the server mints the same sequences, so
 * writing them here is what lets the optimistic row read back unchanged.
 */
function withMemberIds(content: DiagnosticContent): DiagnosticDefinitionRowContent {
  let componentIndex = 0;
  let optionIndex = 0;
  const value = (input: DiagnosticContent["observations"][number]["components"][number]["value"]): CatalogValueDto =>
    input.kind === "Choice"
      ? {
          ...input,
          options: input.options.map((option) => ({
            id: memberId(option.id, "opt", optionIndex++),
            label: option.label,
          })),
        }
      : { ...input };
  return {
    ...(content.procedureNoteLabel === undefined ? {} : { procedureNoteLabel: content.procedureNoteLabel }),
    // The standards are never authored members and never reach storage: the
    // reader derives them back from the opt-outs the writer stamps.
    standardObservations: content.standardObservations,
    observations: content.observations.map((observation, observationIndex) => ({
      id: memberId(observation.id, "obs", observationIndex),
      label: observation.label,
      repeatable: observation.repeatable,
      ...(observation.noteLabel === undefined ? {} : { noteLabel: observation.noteLabel }),
      components: observation.components.map((component) => ({
        id: memberId(component.id, "cmp", componentIndex++),
        label: component.label,
        value: value(component.value),
      })),
    })),
    parameters: mapAuthored(content.parameters, (parameter, parameterIndex) => ({
      id: memberId(parameter.id, "prm", parameterIndex),
      label: parameter.label,
      value: value(parameter.value),
    })),
    deviceRequirements: content.requirements.devices.map((requirement, requirementIndex) => ({
      id: memberId(requirement.id, "dev", requirementIndex),
      definition: requirement.definition,
    })),
    ...(content.requirements.location === undefined ? {} : { locationRequirement: content.requirements.location }),
  };
}

/**
 * A REAL `ActivityDefinition`, in the shape the engine's encoder writes, plus
 * the derived columns an insert bypasses (`deriveFields` runs at sync only).
 * The projection reads it exactly as it reads the synced row that replaces it.
 */
function optimisticRow(spine: DiagnosticDefinitionRowSpine, content: DiagnosticContent): ActivityDefinitionRow {
  return {
    ...writeDiagnosticDefinitionRow(spine, withMemberIds(content)),
    id: optimisticId(spine.url, spine.version),
    catalogModelKind: DIAGNOSTIC_DEFINITION_KIND,
    catalogVersion: spine.version,
    definitionCanonical: `${spine.url}|${spine.version}`,
    catalogViewScope: "catalog-definitions",
  };
}

function currentRow(definitionUrl: string): ActivityDefinitionRow & { id: string } {
  const row = [...activityDefinitions().state.values()]
    .filter((candidate) => candidate.url === definitionUrl && candidate.catalogModelKind === DIAGNOSTIC_DEFINITION_KIND)
    .sort((left, right) => right.catalogVersion - left.catalogVersion)[0];
  if (!row?.id) throw new Error(`Diagnostic Definition '${definitionUrl}' is not available in the data store`);
  return { ...row, id: row.id };
}

export const defineDiagnosticDefinition = defineWrite<DefineDiagnosticDefinitionInput>({
  optimistic: (params) => {
    const input = defineDiagnosticDefinitionInputSchema.parse(params);
    activityDefinitions().insert(
      optimisticRow(
        {
          url: input.definitionUrl,
          version: 1,
          mintedAt: new Date().toISOString(),
          name: input.name,
          ...(input.description === undefined ? {} : { description: input.description }),
          availability: "Active",
        },
        input,
      ),
    );
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.diagnosticDefinition.define.mutate(input, options);
  },
});

export const reviseDiagnosticDefinition = defineWrite<ReviseDiagnosticDefinitionInput>({
  optimistic: (params) => {
    const input = reviseDiagnosticDefinitionInputSchema.parse(params);
    const current = projectDiagnosticDefinitionRow(currentRow(input.definitionUrl));
    activityDefinitions().insert(
      optimisticRow(
        {
          url: current.definitionUrl,
          version: input.expectedVersion + 1,
          mintedAt: new Date().toISOString(),
          name: current.name,
          ...(current.description === undefined ? {} : { description: current.description }),
          availability: current.availability,
        },
        input,
      ),
    );
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.diagnosticDefinition.revise.mutate(input, options);
  },
});

export const correctDiagnosticDefinition = defineWrite<CorrectDiagnosticDefinitionInput>({
  optimistic: (params) => {
    const input = correctDiagnosticDefinitionInputSchema.parse(params);
    const row = currentRow(input.definitionUrl);
    activityDefinitions().update(syncKey(row), (draft) => {
      if (input.name !== undefined) Object.assign(draft, catalogNameToFhir(input.name));
      if (input.description === null) delete draft.description;
      else if (input.description !== undefined) draft.description = input.description;
    });
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.diagnosticDefinition.correct.mutate(input, options);
  },
});

function availabilityAction(availability: DiagnosticDefinitionAvailability) {
  return defineWrite<ChangeDiagnosticDefinitionAvailabilityInput>({
    optimistic: (params) => {
      const input = changeDiagnosticDefinitionAvailabilityInputSchema.parse(params);
      const row = currentRow(input.definitionUrl);
      activityDefinitions().update(syncKey(row), (draft) => {
        draft.status = availability === "Active" ? "active" : "retired";
      });
    },
    persist: async (input, options) => {
      const mutation =
        availability === "Active"
          ? getTRPCClient().catalog.diagnosticDefinition.activate
          : getTRPCClient().catalog.diagnosticDefinition.deactivate;
      await mutation.mutate(input, options);
    },
  });
}

export const activateDiagnosticDefinition = availabilityAction("Active");
export const deactivateDiagnosticDefinition = availabilityAction("Inactive");
